import { Icon } from '@iconify/react';
import { styled } from '@linaria/react';
import { Loader, Tooltip } from '@mantine/core';
import { icons } from '../../../../assets/icons/icons';
import { isSafeUrl } from '../../../../helpers/general-helpers';
import { Text } from '../../../../ui-library/typography/typography';
import { isTextPreviewDocType } from '../../../flora/components/flora-document/flora-document.helpers';
import type { UnifiedDocument } from './agent-document.helpers';
import { isPreviewable } from './google-file-preview.helpers';

/** Callbacks the Documents surfaces wire into each row/card's actions. */
interface DocumentActionHandlers {
  /** Preview a Google file in the embedded modal. */
  onPreview: (doc: UnifiedDocument) => void;
  /** Preview a Flora document (or a specific revision) in the PDF modal. */
  onPreviewFlora: (doc: UnifiedDocument, revision?: number) => void;
  /** Open a Google file in Google (new tab). */
  onOpenGoogle: (doc: UnifiedDocument) => void;
  /** Jump to the chat that produced a Flora document. */
  onGoToChat: (doc: UnifiedDocument) => void;
  /** Download a Flora-generated document. */
  onDownload: (doc: UnifiedDocument) => void;
  /** Unified id of the document currently downloading, if any. */
  downloadingId: string | null;
}

/**
 * Whether a Flora document has an in-app preview: a Flora-generated (not
 * uploaded) Word/Excel document shown as a PDF, or a CSV, TSV, Markdown, JSON,
 * or text document of any origin rendered from its text.
 *
 * Text types are safe for uploads too because they are rendered as text by
 * React, never framed or injected as HTML.
 *
 * This gate is deliberate, not incidental. The preview endpoint presigns and
 * returns ANY document whose `doc_type` is `pdf` before it reaches its
 * Flora-generated check, so a user-uploaded PDF would be served happily — and
 * the preview frames render without a `sandbox` attribute. Uploaded-PDF preview
 * is not a supported feature; if it is ever added, the frames must be sandboxed
 * BEFORE the gate is widened, because a PDF is an active-content format.
 *
 * @param {UnifiedDocument} doc - The unified document to test
 * @returns {boolean} True for a Flora-generated Word/Excel document or any Flora text document
 */
function isFloraPreviewable(doc: UnifiedDocument): boolean {
  if (doc.source !== 'flora') return false;
  if (isTextPreviewDocType(doc.typeKey)) return true;
  return doc.floraSource === 'flora_generated' && (doc.typeKey === 'docx' || doc.typeKey === 'xlsx');
}

export { isFloraPreviewable };

/** The distinct origin chips a document can carry. */
type DocumentBadgeKind = 'flora' | 'uploaded' | 'google';

/**
 * Resolve a document's origin chip: Google Drive files, user-uploaded Flora
 * documents, or Flora-generated documents.
 *
 * The "Uploaded" chip requires a positive `floraSource === 'uploaded'` signal.
 * Everything else Flora-side — a known `flora_generated`, an unknown future
 * value, or an absent `source` (older payloads predate the uploads feature, so
 * they are Flora-generated) — resolves to the plain "Flora" chip. The upload
 * endpoint always stamps `source: 'uploaded'`, so this default does not mislabel
 * real uploads; it just keeps legacy/unknown docs on the neutral Flora chip
 * rather than rendering an empty one.
 *
 * @param {UnifiedDocument} doc - The unified document
 * @returns {DocumentBadgeKind} Which origin chip to show
 */
function documentBadgeKind(doc: UnifiedDocument): DocumentBadgeKind {
  if (doc.source === 'google') return 'google';
  if (doc.floraSource === 'uploaded') return 'uploaded';
  return 'flora';
}

/**
 * Origin pill for a document — a Flora gradient chip, an "Uploaded" chip for
 * user-uploaded files, or a Google Drive chip — shown on cards and in the
 * table's Source column.
 *
 * @param {{ doc: UnifiedDocument }} props - The unified document.
 * @returns {JSX.Element} The labeled origin badge.
 */
export function DocumentSourceBadge({ doc }: { doc: UnifiedDocument }) {
  const kind = documentBadgeKind(doc);
  return (
    <Badge $kind={kind}>
      {kind === 'flora' && <FloraIcon src={icons.iconFlora} alt="" />}
      {kind === 'uploaded' && <Icon icon="material-symbols:upload" width={12} height={12} color="var(--ink-light)" />}
      {kind === 'google' && <Icon icon="logos:google-drive" width={11} height={11} />}
      <BadgeText $kind={kind} size="tiny" weight="bold">
        {kind === 'flora' ? 'Flora' : kind === 'uploaded' ? 'Uploaded' : 'Google Drive'}
      </BadgeText>
    </Badge>
  );
}

/**
 * The per-document action buttons, keyed off source: Flora documents offer
 * go-to-chat + download; Google files offer preview + open-in-Google. Buttons
 * are omitted when their target is unavailable (no chat id, unsafe url, or a
 * non-previewable file).
 *
 * @param {{ doc: UnifiedDocument; handlers: DocumentActionHandlers }} props - The document and action callbacks.
 * @returns {JSX.Element} The action button group.
 */
export function DocumentActions({ doc, handlers }: { doc: UnifiedDocument; handlers: DocumentActionHandlers }) {
  const { onPreview, onPreviewFlora, onOpenGoogle, onGoToChat, onDownload, downloadingId } = handlers;

  if (doc.source === 'flora') {
    const isDownloading = downloadingId === doc.id;
    return (
      <>
        {isFloraPreviewable(doc) && (
          <Tooltip label="Preview" position="bottom" withArrow>
            <ActionBtn onClick={() => onPreviewFlora(doc)} aria-label={`Preview ${doc.name}`}>
              <Icon icon="material-symbols:visibility-outline" width={16} height={16} />
            </ActionBtn>
          </Tooltip>
        )}
        {doc.flora?.chat_history_id && (
          <Tooltip label="Go to chat" position="bottom" withArrow>
            <ActionBtn onClick={() => onGoToChat(doc)} aria-label={`Go to chat for ${doc.name}`}>
              <Icon icon="material-symbols:chat-outline" width={16} height={16} />
            </ActionBtn>
          </Tooltip>
        )}
        <Tooltip label="Download" position="bottom" withArrow>
          <ActionBtn
            onClick={() => !isDownloading && onDownload(doc)}
            aria-label={`Download ${doc.name}`}
            disabled={isDownloading}
          >
            {isDownloading ? (
              <Loader size={14} color="var(--ink-base)" />
            ) : (
              <Icon icon="material-symbols:download" width={16} height={16} />
            )}
          </ActionBtn>
        </Tooltip>
      </>
    );
  }

  const file = doc.google;
  return (
    <>
      {file && isPreviewable(file) && (
        <Tooltip label="Preview" position="bottom" withArrow>
          <ActionBtn onClick={() => onPreview(doc)} aria-label={`Preview ${doc.name}`}>
            <Icon icon="material-symbols:visibility-outline" width={16} height={16} />
          </ActionBtn>
        </Tooltip>
      )}
      {file && isSafeUrl(file.webViewLink) && (
        <Tooltip label="Open in Google" position="bottom" withArrow>
          <ActionBtn onClick={() => onOpenGoogle(doc)} aria-label={`Open ${doc.name} in Google`}>
            <Icon icon="material-symbols:open-in-new" width={16} height={16} />
          </ActionBtn>
        </Tooltip>
      )}
    </>
  );
}

/** Background tint for a badge kind: the Flora chip vs the neutral chip. */
function getBadgeBackground(kind: DocumentBadgeKind): string {
  return kind === 'flora' ? 'var(--secondary-lightest)' : 'var(--sky-lighter)';
}

/** Text color for a badge kind: the Flora chip vs the neutral chip. */
function getBadgeTextColor(kind: DocumentBadgeKind): string {
  return kind === 'flora' ? 'var(--secondary-darkest)' : 'var(--ink-light)';
}

const Badge = styled.span<{ $kind: DocumentBadgeKind }>`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 8px;
  border-radius: 20px;
  white-space: nowrap;
  background: ${({ $kind }) => getBadgeBackground($kind)};
`;

const BadgeText = styled(Text)<{ $kind: DocumentBadgeKind }>`
  color: ${({ $kind }) => getBadgeTextColor($kind)};
`;

const FloraIcon = styled.img`
  width: 12px;
  height: 12px;
  display: block;
`;

const ActionBtn = styled.button`
  width: 30px;
  height: 30px;
  border-radius: 6px;
  border: none;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: var(--ink-lighter);
  transition: background 0.12s, color 0.12s;
  &:hover:not(:disabled) {
    background: var(--sky-lighter);
    color: var(--ink-dark);
  }
  &:disabled {
    cursor: default;
  }
`;

export type { DocumentActionHandlers };
